/*
 *  Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { ComponentProps } from "react";
import { Unless } from "react-if";
import { Box, Button, FlexContainer, theme } from "@nordcloud/gnui";
import { Provider, SearchV2SortField } from "~/generated/graphql";
import { SearchBoxContainer, SortDirection } from "~/components";
import { useToggle } from "~/hooks";
import { useCustomers } from "~/services/customers";
import { AttachToEnv } from "../../AttachToEnv";
import { ExportV2 } from "../../ExportV2";
import { EstateSortV2 } from "../EstateSortV2";
import { ESTATE_SORT_OPTIONS } from "./types";

type Props = {
  search: string;
  setSearch: (value: string) => void;
  disabledAttachment: boolean;
  sortField: string;
  sortDirection: SortDirection;
  onSortSelect: (value: SearchV2SortField) => void;
  onSortOrderChange: (value: SortDirection) => void;
  resources: {
    nid: string;
    provider: Provider;
  }[];
  queryFilters: ComponentProps<typeof ExportV2>["filters"];
  totalRecords: number;
  refresh?: () => void;
  disabled?: boolean;
};

export function ParametersBar({
  search,
  setSearch,
  disabledAttachment,
  sortField,
  sortDirection,
  onSortSelect,
  onSortOrderChange,
  resources,
  queryFilters,
  totalRecords,
  refresh,
  disabled = false,
}: Props) {
  const [showAttachmentSidebar, toggleShowAttachmentSidebar] = useToggle(false);
  const { isAdminLite } = useCustomers();

  return (
    <>
      <Box boxStyle="lightGrey" marginBottom={theme.spacing.spacing04}>
        <FlexContainer justifyContent="space-between">
          <FlexContainer
            alignItems="center"
            wrap="wrap"
            gap={theme.spacing.spacing02}
          >
            <span
              css={{
                marginRight: theme.spacing.spacing04,
              }}
            >
              <EstateSortV2<SearchV2SortField>
                sortField={sortField}
                sortDirection={sortDirection}
                options={ESTATE_SORT_OPTIONS}
                disabled={disabled}
                onSortOrderChange={onSortOrderChange}
                onSortSelect={onSortSelect}
              />
            </span>
            <SearchBoxContainer
              searchInput={search}
              setSearchInput={setSearch}
              width="20rem"
              disabled={disabled}
            />
          </FlexContainer>
          <FlexContainer
            alignItems="center"
            wrap="wrap"
            gap={theme.spacing.spacing02}
          >
            <Unless condition={isAdminLite}>
              <Button
                disabled={disabledAttachment || disabled}
                icon="link"
                marginRight={theme.spacing.spacing04}
                css={{ textTransform: "none" }}
                onClick={toggleShowAttachmentSidebar}
              >
                Attach to Environments
              </Button>
            </Unless>
            <ExportV2 filters={queryFilters} recordsCount={totalRecords} />
          </FlexContainer>
        </FlexContainer>
      </Box>
      <AttachToEnv
        allowMultipleAssignment
        resources={resources}
        refresh={refresh}
        isOpen={showAttachmentSidebar}
        saveSelections={false}
        onClose={toggleShowAttachmentSidebar}
      />
    </>
  );
}
